/*=========================================================================================
  File Name: vsPopupA11y.js
  Description: Global mixin that gives vuesax's <vs-popup> the dialog semantics and
               keyboard behaviour it ships without.

               vuesax renders `.vs-popup` with no role, no aria-modal and no accessible
               name, and its close control is an <i> element - not focusable, no label, no
               key handling. Escape does not close it either. That is the audit's
               "Dialog box not marked up" (34.1 - 34.3) and "Scripted button not keyboard
               focusable" (26.x) across all 7 vs-popup instances in the app.

               Enhancing the rendered DOM here, rather than forking the component or
               replacing 7 call sites, keeps the fix in one place. Closing is done by
               clicking vuesax's own close element so its internal state stays correct -
               nothing depends on vuesax private API.

               Note vs-popup renders into the DOM even while closed (App.vue mounts
               HelpPopup permanently), so the static ARIA is applied once on mount but
               focus is only moved, and key handling only attached, while `active` is true.
==========================================================================================*/

import { FOCUSABLE } from '@/mixins/dialog';

const VS_POPUP = 'VsPopup';

const visible = (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed';

const focusables = (root) => Array.from(root.querySelectorAll(FOCUSABLE)).filter(visible);

let titleSeq = 0;

// --- static markup: safe to apply whether the popup is open or closed -------------------
function applyAria(root) {
  if (root.dataset.a11yEnhanced === 'true') return;
  root.dataset.a11yEnhanced = 'true';

  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');

  const title = root.querySelector('.vs-popup--title h3, .vs-popup--title');
  if (title) {
    if (!title.id) {
      titleSeq += 1;
      title.id = `vs-popup-title-${titleSeq}`;
    }
    root.setAttribute('aria-labelledby', title.id);
  } else {
    root.setAttribute('aria-label', 'Dialog');
  }

  // vuesax's close control is a decorative <i>: give it a button's role, make it
  // reachable, name it, and let Enter/Space activate it like a real button.
  const close = root.querySelector('.vs-popup--close');
  if (close && close.tagName !== 'BUTTON') {
    close.setAttribute('role', 'button');
    close.setAttribute('tabindex', '0');
    close.setAttribute('aria-label', title ? `Close ${title.textContent.trim()}` : 'Close dialog');
    close.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        close.click();
      }
    });
  }
}

const popupRoot = (vm) =>
  vm.$el && vm.$el.querySelector ? vm.$el.querySelector('.vs-popup') : null;

function openDialog(vm) {
  const root = popupRoot(vm);
  if (!root || vm._a11yKeydown) return;

  vm._a11yReturnFocus = document.activeElement;

  vm._a11yKeydown = (event) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      const close = root.querySelector('.vs-popup--close');
      if (close) close.click();
      return;
    }
    if (event.key !== 'Tab') return;

    const targets = focusables(root);
    if (!targets.length) return;
    const first = targets[0];
    const last = targets[targets.length - 1];
    const active = document.activeElement;

    if (!root.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Listening on the document, not the popup: Escape has to work before the user has
  // tabbed into the dialog.
  document.addEventListener('keydown', vm._a11yKeydown, true);

  const targets = focusables(root);
  if (targets.length) {
    targets[0].focus();
  } else {
    root.setAttribute('tabindex', '-1');
    root.focus();
  }
}

function closeDialog(vm) {
  if (vm._a11yKeydown) {
    document.removeEventListener('keydown', vm._a11yKeydown, true);
    vm._a11yKeydown = null;
  }
  const el = vm._a11yReturnFocus;
  if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
  vm._a11yReturnFocus = null;
}

export default {
  mounted() {
    if (this.$options.name !== VS_POPUP) return;

    this.$nextTick(() => {
      const root = popupRoot(this);
      if (root) applyAria(root);
      if (this.active) this.$nextTick(() => openDialog(this));
    });

    this.$watch('active', (isOpen) => {
      this.$nextTick(() => {
        const root = popupRoot(this);
        if (root) applyAria(root);
        if (isOpen) openDialog(this);
        else closeDialog(this);
      });
    });
  },

  beforeDestroy() {
    if (this.$options.name === VS_POPUP) closeDialog(this);
  },
};
